import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Code,
  Brain,
  BarChart3,
  Palette,
  MessageCircle,
  Briefcase,
  Award,
  Users,
  BookOpen,
  CheckCircle2,
  ArrowRight,
  GraduationCap,
  Target,
  TrendingUp
} from 'lucide-react';

const TrainingPlacement = () => {
  const trainingPrograms = [
    {
      category: "Technical Training",
      icon: Code,
      courses: [
        "Full Stack Development (MERN/MEAN)",
        "AI & Machine Learning Basics",
        "Data Analytics & Visualization",
        "Python Programming",
        "Digital Marketing & SEO",
        "Graphic Designing & UI/UX"
      ]
    },
    {
      category: "Soft Skills",
      icon: MessageCircle,
      courses: [
        "Spoken English & Communication",
        "Interview Preparation",
        "Personality Development",
        "Leadership & Team Management",
        "Presentation Skills",
        "Time Management"
      ]
    },
    {
      category: "Business Software",
      icon: Briefcase,
      courses: [
        "ERP/CRM Training",
        "Customer Support Excellence",
        "Sales & Negotiation Skills",
        "Office Productivity Tools",
        "Project Management",
        "Business Analytics"
      ]
    }
  ];

  const placementServices = [
    {
      icon: Users,
      title: "Campus Hiring Support",
      description: "Partnering with educational institutions for campus placements"
    },
    {
      icon: Briefcase,
      title: "Job Assistance",
      description: "Dedicated placement support with our network of companies"
    },
    {
      icon: BookOpen,
      title: "Resume Building",
      description: "Professional resume writing and LinkedIn optimization"
    },
    {
      icon: MessageCircle,
      title: "Mock Interviews",
      description: "Practice sessions with industry experts and feedback"
    }
  ];

  const features = [
    "Live interactive sessions",
    "Industry expert trainers",
    "Hands-on projects",
    "Internship opportunities",
    "Certification on completion",
    "Lifetime course access",
    "Job placement assistance",
    "Flexible batch timings"
  ];

  const targetAudience = [
    { icon: GraduationCap, title: "Students", desc: "College students seeking career skills" },
    { icon: TrendingUp, title: "Freshers", desc: "Recent graduates entering job market" },
    { icon: Users, title: "Working Professionals", desc: "Upskilling for career growth" }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-white to-cyan-50">
        <div className="max-w-7xl mx-auto text-center">
          <Badge className="bg-blue-100 text-blue-700 mb-4">Training & Placement</Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            Advance Your Career with{' '}
            <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">
              Industry-Ready Skills
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8 leading-relaxed">
            Comprehensive training programs in technology, soft skills, and business software 
            with dedicated placement assistance to kickstart your career.
          </p>
          <Link to="/contact">
            <Button className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white px-8 py-6 text-lg">
              Enroll Today
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Training Programs */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Our Training Programs</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Carefully designed courses to make you job-ready
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {trainingPrograms.map((program, index) => (
              <Card key={index} className="p-8 hover:shadow-xl transition-all duration-300 border-2 hover:border-blue-200">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-xl flex items-center justify-center mb-6">
                  <program.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">{program.category}</h3>
                <ul className="space-y-3">
                  {program.courses.map((course, i) => (
                    <li key={i} className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{course}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Placement Services */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Placement Services</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We don't just train you - we help you land your dream job
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {placementServices.map((service, index) => (
              <Card key={index} className="p-8 text-center hover:shadow-xl transition-all duration-300 border-2 hover:border-blue-200 bg-white">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <service.icon className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Course Features */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                What Makes Our Training Different?
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                We focus on practical, industry-relevant skills that employers actually look for. 
                Our training methodology ensures you're job-ready from day one.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <Card className="p-6 text-center bg-gradient-to-br from-blue-50 to-white border-2 border-blue-100">
                <Award className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Certified</h3>
                <p className="text-gray-600">Industry-recognized certificates</p>
              </Card>
              <Card className="p-6 text-center bg-gradient-to-br from-blue-50 to-white border-2 border-blue-100">
                <Brain className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Practical</h3>
                <p className="text-gray-600">Hands-on learning approach</p>
              </Card>
              <Card className="p-6 text-center bg-gradient-to-br from-blue-50 to-white border-2 border-blue-100">
                <Users className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Expert</h3>
                <p className="text-gray-600">Learn from industry pros</p>
              </Card>
              <Card className="p-6 text-center bg-gradient-to-br from-blue-50 to-white border-2 border-blue-100">
                <Target className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Placement</h3>
                <p className="text-gray-600">Job assistance guaranteed</p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Target Audience */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Who Can Join?</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our programs are designed for diverse learners at different career stages
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {targetAudience.map((audience, index) => (
              <Card key={index} className="p-10 text-center hover:shadow-xl transition-all duration-300 border-2 hover:border-blue-200 bg-white">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <audience.icon className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">{audience.title}</h3>
                <p className="text-gray-600">{audience.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Special Programs */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Special Programs</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Card className="p-10 bg-gradient-to-br from-blue-50 to-white border-2 border-blue-100">
              <GraduationCap className="w-14 h-14 text-blue-600 mb-6" />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Internship Programs</h3>
              <p className="text-gray-600 mb-6">
                Get real-world experience with our internship opportunities in various domains. 
                Work on live projects and build your portfolio.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">3-6 month duration</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Live project experience</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Certificate of completion</span>
                </li>
              </ul>
            </Card>

            <Card className="p-10 bg-gradient-to-br from-blue-50 to-white border-2 border-blue-100">
              <Award className="w-14 h-14 text-blue-600 mb-6" />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Certification Programs</h3>
              <p className="text-gray-600 mb-6">
                Earn industry-recognized certifications that add value to your resume and 
                showcase your expertise to potential employers.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Industry-recognized certificates</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Skill validation</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Career advancement</span>
                </li>
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-cyan-500">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Start Your Learning Journey?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Enroll in our programs and take the first step towards your dream career
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-lg rounded-lg">
                Enroll Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:+919315649601">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg rounded-lg">
                Call for Details
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TrainingPlacement;
